import { SerializedMessage, SerializedContent } from '@/components/sms-editor/utils/serializer';
import { ImageIcon, ContactIcon } from 'lucide-react';

// Import phone frame SVG
import PhoneDarkSVG from '@/routes/campaigns/assets/Phone_Dark.svg';

interface MediaPreviewProps {
  type: 'image' | 'contact';
  url?: string;
  caption?: string;
}

function MediaPreview({ type, url, caption }: MediaPreviewProps) {
  return (
    <div className="max-w-[50%] bg-muted rounded-2xl overflow-hidden">
      {type === 'image' ? (
        <div className="flex flex-col">
          {url ? (
            <div className="w-full aspect-[4/3] bg-muted-foreground/10">
              <img src={url} alt="MMS attachment" className="w-full h-full object-cover" />
            </div>
          ) : (
            <div className="w-full aspect-[4/3] bg-muted-foreground/10 flex items-center justify-center">
              <ImageIcon className="w-8 h-8 text-muted-foreground/50" />
            </div>
          )}
          <div className="p-2.5 text-sm">{caption ?? 'Image Attachment'}</div>
        </div>
      ) : (
        <div className="p-2.5 text-sm flex items-center gap-2">
          <div className="w-10 h-10 rounded bg-muted-foreground/10 flex items-center justify-center flex-shrink-0">
            <ContactIcon className="w-5 h-5 text-muted-foreground/50" />
          </div>
          <div className="flex-1">{caption ?? 'Contact Card'}</div>
        </div>
      )}
    </div>
  );
}

interface MessagePreviewProps {
  content: SerializedContent;
}

export function MessagePreview({ content }: MessagePreviewProps) {
  let messages: SerializedMessage[] = [];

  try {
    const parsed = content;
    messages = parsed.messages;
  } catch (e) {
    console.debug('Error parsing message content:', e);
    // Return empty message placeholder
    return (
      <div className="w-full">
        <div className="mx-auto max-w-[365px]">
          <div className="relative">
            {/* Phone Frame SVG */}
            <img src={PhoneDarkSVG} alt="Phone frame" className="w-full" />

            {/* Content Container */}
            <div className="absolute top-[115px] bottom-[0px] left-[12px] right-[6px] rounded-3xl overflow-hidden">
              {/* Message Area */}
              <div className="h-full overflow-y-auto px-4">
                <div className="max-w-[75%] bg-muted rounded-3xl py-2.5 px-4 text-sm text-muted-foreground">
                  Your message will appear here...
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="mx-auto max-w-[365px]">
        <div className="relative">
          {/* Phone Frame SVG */}
          <img src={PhoneDarkSVG} alt="Phone frame" className="w-full" />

          {/* Content Container */}
          <div className="absolute top-[115px] bottom-[0px] left-[12px] right-[6px] rounded-3xl overflow-hidden">
            {/* Message Area */}
            <div className="h-full overflow-y-auto px-4">
              {messages.map((message, index) => {
                switch (message.type) {
                  case 'text':
                    return message.content.text ? (
                      <div key={index} className="mb-2">
                        <div className="max-w-[75%] bg-muted rounded-3xl py-2.5 px-4 text-sm whitespace-pre-wrap break-words">
                          {message.content.text}
                        </div>
                      </div>
                    ) : null;

                  case 'media':
                    return (
                      <div className="mb-2">
                        <MediaPreview key={index} type="image" url={message.content.url} />
                      </div>
                    );

                  case 'media_card':
                    return (
                      <div className="mb-2">
                        <MediaPreview
                          key={index}
                          type="contact"
                          url={message.content.url}
                          caption={message.content.caption}
                        />
                      </div>
                    );

                  default:
                    return null;
                }
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
